import { useSyncExternalStore } from "react";

/** Tailwind デフォルトブレークポイント (px) */
const BREAKPOINTS = {
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    "2xl": 1536,
} as const;

type BreakpointKey = keyof typeof BREAKPOINTS;

function createMediaQuery(minWidth: number): MediaQueryList {
    return window.matchMedia(`(min-width: ${minWidth}px)`);
}

/** 各ブレークポイントの match 状態をまとめて返すスナップショット */
function getSnapshot(): Record<BreakpointKey, boolean> {
    return {
        sm: createMediaQuery(BREAKPOINTS.sm).matches,
        md: createMediaQuery(BREAKPOINTS.md).matches,
        lg: createMediaQuery(BREAKPOINTS.lg).matches,
        xl: createMediaQuery(BREAKPOINTS.xl).matches,
        "2xl": createMediaQuery(BREAKPOINTS["2xl"]).matches,
    };
}

/** SSR 用フォールバック（デスクトップ想定） */
function getServerSnapshot(): Record<BreakpointKey, boolean> {
    return { sm: true, md: true, lg: true, xl: true, "2xl": true };
}

/** キャッシュ: 同じ match 結果なら同一オブジェクトを返してリレンダリングを抑制 */
let cached: Record<BreakpointKey, boolean> | null = null;

function getCachedSnapshot(): Record<BreakpointKey, boolean> {
    const next = getSnapshot();
    if (
        cached &&
        cached.sm === next.sm &&
        cached.md === next.md &&
        cached.lg === next.lg &&
        cached.xl === next.xl &&
        cached["2xl"] === next["2xl"]
    ) {
        return cached;
    }
    cached = next;
    return cached;
}

function subscribe(callback: () => void): () => void {
    const queries = Object.values(BREAKPOINTS).map(createMediaQuery);
    for (const mq of queries) {
        mq.addEventListener("change", callback);
    }
    return () => {
        for (const mq of queries) {
            mq.removeEventListener("change", callback);
        }
    };
}

interface BreakpointResult {
    /** 640px 以上 */
    isSm: boolean;
    /** 768px 以上 */
    isMd: boolean;
    /** 1024px 以上 */
    isLg: boolean;
    /** 1280px 以上 */
    isXl: boolean;
    /** 1536px 以上 */
    is2xl: boolean;
    /** タブレット: md 以上 lg 未満 (768–1023px) */
    isTablet: boolean;
    /** デスクトップ: lg 以上 (1024px~) */
    isDesktop: boolean;
}

/**
 * Tailwind ブレークポイントに連動するレスポンシブフック。
 * CSS で対応できない JS ロジックの分岐にのみ使用する。
 */
export function useBreakpoint(): BreakpointResult {
    const bp = useSyncExternalStore(subscribe, getCachedSnapshot, getServerSnapshot);

    return {
        isSm: bp.sm,
        isMd: bp.md,
        isLg: bp.lg,
        isXl: bp.xl,
        is2xl: bp["2xl"],
        isTablet: bp.md && !bp.lg,
        isDesktop: bp.lg,
    };
}
